import {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
    useCallback,
} from 'react'
import { getAppConfig, ConfigSettings } from '../api/api'
import { AuthStrategy } from '../authConfig'

interface ConfigContextType {
    asktextfieldplaceholder?: string
    asksubtitle?: string
    contributetitle?: string
    contributeclausetext?: string
    communityhubname?: string
    communityhubbio?: string
    termsandconditionslink?: string
    logoUrl?: string | null
    ask?: boolean
    contribute?: boolean
    profilepage?: boolean
    communityHub?: boolean
    nonFastTranscriptionApiMic?: boolean
    engage?: boolean
    captures?: boolean
    knowledgeBio?: string
    knowledgeRules?: string[]
    isLoading: boolean
    // Auth setup — derived from server config + tenant requires_auth flag
    allowAnonymous: boolean
    requiresAuth: boolean
    authStrategy: AuthStrategy
    // Per-page anonymous access rules (server-controlled, deny-by-default for auth-required pages)
    anonymousPageAccess: {
        chat: boolean
        community: boolean
        contribute: boolean
        engage: boolean
        exchange: boolean
    }
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined)

export function ConfigProvider({ children }: { children: ReactNode }) {
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [configSettings, setConfigSettings] = useState<
        ConfigSettings | undefined
    >(undefined)

    // Fetched once, when the signed-in app mounts (PrivateRoutes), and never
    // again for the life of the page. It used to refetch on every window focus.
    // On a bad connection that refetch failed, `getAppConfig` answered
    // `undefined`, every flag read as off, and the page wrappers redirected away
    // from whatever was open, including a capture mid-recording. A flag change
    // made in super admin now shows on the next load; the backend still refuses
    // what a tenant has switched off, so showing a page a little longer is safe.
    const fetchConfigData = useCallback(async () => {
        try {
            const data = await getAppConfig()
            // No answer is not an answer: never replace config with nothing.
            if (data) {
                setConfigSettings(data)
            }

            if (data?.data?.settings?.brandColors) {
                applyBrandColors(data.data.settings.brandColors)
            }

            setIsLoading(false)
        } catch (error) {
            console.error('Error fetching config settings:', error)
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchConfigData()
    }, [fetchConfigData])

    // Auth setup from server — defaults are permissive (allow anonymous) until config loads
    const authSetup = configSettings?.data?.authSetup ?? {
        allowAnonymous: true,
        requiresAuth: false,
        strategy: 'email_auth_with_anon' as AuthStrategy,
    }

    // Anonymous page access — secure by default: deny every page until the
    // server config explicitly opts one in (matches backend ANON_PAGE_ACCESS_DEFAULTS).
    const defaultAnonAccess = {
        chat: false,
        community: false,
        contribute: false,
        engage: false,
        exchange: false,
    }
    const anonymousPageAccess = {
        ...defaultAnonAccess,
        ...configSettings?.data?.settings?.anonymousPageAccess,
    }

    const value: ConfigContextType = {
        asktextfieldplaceholder:
            configSettings?.data?.settings?.askTextFieldPlaceholder,
        asksubtitle: configSettings?.data?.settings?.askSubtitle,
        contributetitle: configSettings?.data?.settings?.contributeTitle,
        contributeclausetext:
            configSettings?.data?.settings?.contributeClauseText,
        communityhubname: configSettings?.data?.settings?.communityHubName,
        communityhubbio: configSettings?.data?.settings?.communityHubBio,
        termsandconditionslink:
            configSettings?.data?.settings?.termsAndConditionsLink,
        logoUrl: configSettings?.data?.settings?.logoUrl,
        ask: configSettings?.data?.settings?.askPage,
        contribute: configSettings?.data?.settings?.contributePage,
        profilepage: configSettings?.data?.settings?.profilePage,
        communityHub: configSettings?.data?.settings?.communityHub,
        nonFastTranscriptionApiMic:
            configSettings?.data?.settings?.nonFastTranscriptionApiMic,
        engage: configSettings?.data?.settings?.engagePage,
        captures: configSettings?.data?.settings?.capturesPage,
        knowledgeBio: configSettings?.data?.settings?.knowledgeBio,
        knowledgeRules: configSettings?.data?.settings?.knowledgeRules,
        isLoading,
        allowAnonymous: authSetup.allowAnonymous,
        requiresAuth: authSetup.requiresAuth,
        authStrategy: authSetup.strategy as AuthStrategy,
        anonymousPageAccess,
    }

    return (
        <ConfigContext.Provider value={value}>
            {children}
        </ConfigContext.Provider>
    )
}

export function useConfig() {
    const context = useContext(ConfigContext)
    if (context === undefined) {
        throw new Error('useConfig must be used within ConfigProvider')
    }
    return context
}

function hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    if (!result) return '0, 0, 0'
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
}

function applyBrandColors(brandColorsJson: string) {
    try {
        const colors = JSON.parse(brandColorsJson)
        const root = document.documentElement

        Object.entries(colors).forEach(([key, hex]) => {
            root.style.setProperty(`--${key}`, hex as string)
            root.style.setProperty(`--${key}-rgb`, hexToRgb(hex as string))
        })
    } catch (error) {
        console.error('Failed to parse brand colours:', error)
    }
}
